import { createFileRoute, Link } from "@tanstack/react-router";
import type { LoggedBundleItem, ServiceBundleItemWithService } from "@somasi/shared";
import { apportionLoggedBundle, loggedServiceLines, serviceEventBundleKind } from "@somasi/shared";
import { Fragment, useEffect, useMemo, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { api, type ListBuilder } from "@/integrations/aws/resources";
import { useConfirmDelete } from "@/components/confirm-dialog";
import { PageHeader } from "@/components/app-shell";
import { Button } from "@/components/ui/button";
import { RowAction, RowActions } from "@/components/row-actions";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge, badgeVariants } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { cn } from "@/lib/utils";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Boxes,
  Check,
  ChevronDown,
  ChevronRight,
  ChevronsUpDown,
  Download,
  Plus,
  Trash2,
  X,
} from "lucide-react";
import { notify } from "@/lib/notify";
import { money, resolveCompanyPriceList, type ResolvedService } from "@/lib/billing";
import { BUNDLE_RATE_LABEL, bestPublishedRate, type BundleRateOrigin } from "@/lib/bundle-pricing";
import { fetchInvoiceStamps, invoiceStampCells, useInvoiceStamps } from "@/lib/invoices";
import { useAuth } from "@/lib/auth";
import {
  useRowSelection,
  BulkBar,
  SelectAllCheckbox,
  RowCheckbox,
} from "@/components/bulk-actions";
import {
  DateRangeFilter,
  describeDateRange,
  EMPTY_DATE_RANGE,
  FilterSelect,
  ListPage,
  ListPageHeader,
  ListPageTable,
  ListSearch,
  ListToolbar,
  SortHeader,
  TableMessageRow,
  TablePagination,
  useDebounced,
  useListSort,
  usePagination,
  type DateRange,
} from "@/components/list-controls";
import {
  ClientGroupSelect,
  CompanySelect,
  ServiceBundleSelect,
  ServiceSelect,
  fetchEntityNames,
  useEntityNames,
  type EntityOption,
  type ServiceRow,
} from "@/components/entity-select";
import { BundlePicker, type PickedBundle } from "@/components/bundle-picker";
import { BilledCell } from "@/components/billed-cell";
import { Truncated } from "@/components/truncated";
import { ImportButton } from "@/components/import-dialog";
import {
  COMPANY_IDENTITY_COLUMNS,
  EXPENSE_HEADERS,
  EXPENSE_EXPORT_COLUMNS,
  EXPENSE_EXPORT_HEADERS,
  SERVICE_EVENT_HEADERS,
  SERVICE_EVENT_EXPORT_COLUMNS,
  SERVICE_EVENT_EXPORT_HEADERS,
  buildCompanyIndex,
  companyLabel,
  expenseTemplateColumns,
  serviceEventTemplateColumns,
  serviceLabel,
  validateExpenseRows,
  validateServiceEventRows,
  type CompanyIdentity,
  type CompanyIndex,
} from "@/lib/work-log-import";
import { SearchableSelect } from "@/components/searchable-select";
import {
  ListExportDialog,
  type ExportRowSet,
  type ExportScope,
} from "@/components/list-export-dialog";

export const Route = createFileRoute("/_authenticated/work-log")({
  head: () => ({
    meta: [
      { title: "Work & Expense Log — Somasi Ops" },
      {
        name: "description",
        content: "Log services delivered and out-of-pocket expenses so nothing goes unbilled.",
      },
      { property: "og:title", content: "Work & Expense Log — Somasi Ops" },
      {
        property: "og:description",
        content: "Log services delivered and out-of-pocket expenses so nothing goes unbilled.",
      },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary" },
    ],
  }),
  component: WorkLogPage,
});

type ServiceEvent = {
  id: string;
  company_id: string;
  service_id: string | null;
  event_date: string;
  quantity: number;
  description: string | null;
  amount: number | null;
  notes: string | null;
  invoice_id: string | null;
  /** Which bundle this was logged from, if any — see `serviceEventBundleKind`. */
  bundle_id: string | null;
  bundle_name: string | null;
  bundle_items: LoggedBundleItem[] | null;
};

type SundryExpense = {
  id: string;
  company_id: string;
  expense_date: string;
  expense_type: string;
  description: string;
  amount: number;
  markup_pct: number;
  invoice_id: string | null;
  notes: string | null;
};

export const EXPENSE_TYPES = ["apostille", "stamp", "courier", "notary", "government fee", "other"];

/**
 * A filter picker's choice: the id the query sends, and the name it chose.
 *
 * The name is kept because the export dialog has to say which set a file holds
 * — "for Alpha Holdings Ltd, dated 2026-10-01 to 2026-10-31" — and a filter bar
 * made of ids cannot say that. Resolving the ids back to names afterwards would
 * be asking the API for strings the pickers had in hand when they were clicked.
 *
 * `""` is "no choice", which is what a picker clears to; the old "all" sentinel
 * existed because a Radix `Select` cannot hold an empty value, and these are not
 * Radix selects.
 */
type Picked = { id: string; label: string };

const NO_PICK: Picked = { id: "", label: "" };

/** `onChange` then `onPick` is how `AsyncSelect` reports a choice; this takes both. */
function pickHandlers(set: (picked: Picked) => void) {
  return {
    onChange: (id: string) => set(id ? { id, label: "" } : NO_PICK),
    onPick: (option: EntityOption<unknown>) => set({ id: option.id, label: option.label }),
  };
}

/** How an export names what shape a bundle was logged in. Blank for neither. */
function bundleKindLabel(event: ServiceEvent): string {
  const kind = serviceEventBundleKind(event);
  if (kind === "grouped") return "One bundled line";
  if (kind === "spread") return "A line per service";
  return "";
}

/**
 * One logged entry as the rows a file should hold — which is not always one row.
 *
 * A file is read the way the invoice is: summed, filtered by service, reconciled
 * against what was billed. So it itemises, and a bundle logged as one line comes
 * out as one row per service in it. Exported as the single row it is stored as,
 * every packaged delivery would be a blank in the Service column and a figure
 * with nothing to attribute it to.
 *
 * `loggedServiceLines` is the decomposition, shared with the invoice builder so
 * the file, the screen and the invoice cannot come to three different answers
 * about what a package covered. Only the three columns that differ per line are
 * written here; everything else is the entry's and is passed in.
 */
function serviceEventExportRows(
  event: ServiceEvent,
  base: Record<string, unknown>,
  serviceNames: Record<string, string>,
): Record<string, unknown>[] {
  return loggedServiceLines(event).map((line) => ({
    ...base,
    [SERVICE_EVENT_HEADERS.service]: line.itemised
      ? (line.service_name ?? "")
      : (line.service_id && serviceNames[line.service_id]) || "",
    [SERVICE_EVENT_HEADERS.quantity]: line.quantity,
    // The rate, matching the column's meaning on every other row — `Qty` times
    // `Amount` is what the row comes to, here as everywhere else.
    [SERVICE_EVENT_HEADERS.amount]: line.unit_amount ?? "",
  }));
}

/**
 * The Service column, which has three things to say rather than one.
 *
 * A bundled entry names no service — it is the package — so the column would read
 * as an empty cell on the one row whose charge most needs explaining. It names
 * the bundle instead, badged with how many services are in it, and opens to show
 * them. A service logged as part of a spread bundle shows the service, and says
 * which bundle it came in underneath: the alternative is five rows that look
 * keyed in by hand, which is exactly the distinction the stamp exists to keep.
 */
function LoggedServiceCell({
  event,
  serviceName,
  open,
  onToggle,
}: {
  event: ServiceEvent;
  serviceName: string;
  open: boolean;
  onToggle: () => void;
}) {
  const kind = serviceEventBundleKind(event);

  if (kind === "grouped") {
    const count = (event.bundle_items ?? []).length;

    // The whole name is the control, not a caret beside it: the row has one thing
    // to open, and a 14px target would be the only fiddly click on the page. An
    // entry with no breakdown stored has nothing to open, and says so by not
    // being a button.
    if (count === 0) {
      return (
        <span className="flex items-center gap-1.5">
          <Boxes className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
          <span className="min-w-0 truncate">{event.bundle_name ?? "Bundle"}</span>
          <Badge variant="secondary" className="shrink-0 text-[9px] font-normal">
            bundle
          </Badge>
        </span>
      );
    }

    return (
      <button
        type="button"
        onClick={onToggle}
        aria-expanded={open}
        className="flex w-full min-w-0 items-center gap-1.5 rounded-sm text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
      >
        {open ? (
          <ChevronDown className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
        ) : (
          <ChevronRight className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
        )}
        <span className="min-w-0 truncate hover:underline">{event.bundle_name ?? "Bundle"}</span>
        <Badge variant="secondary" className="shrink-0 text-[9px] font-normal">
          bundle · {count}
        </Badge>
      </button>
    );
  }

  return (
    <span className="block">
      {serviceName}
      {kind === "spread" && (
        <span className="mt-0.5 flex items-center gap-1 text-xs text-muted-foreground">
          <Boxes className="h-3 w-3 shrink-0" />
          <span className="min-w-0 truncate">{event.bundle_name}</span>
        </span>
      )}
    </span>
  );
}

/**
 * What is in a bundled entry, opened out under the row it belongs to.
 *
 * The figures are the ones the invoice will raise, not the rates the entry was
 * logged at. A package logged at a price its services' rates do not add up to has
 * that difference spread across them, and listing the logged rates here would be
 * listing numbers the client is never going to see. Where that has happened it is
 * said underneath, with both totals, because "why does this service say 720 when
 * it was logged at 900" is the first question the row raises.
 *
 * One cell across the whole width rather than figures lined up under the parent
 * table's columns: these are a different set of columns — a rate per service,
 * which the row above has no column for — and threading them through the outer
 * widths would pin both tables to whichever is wider.
 */
function BundleBreakdownRow({ event, colSpan }: { event: ServiceEvent; colSpan: number }) {
  const items = event.bundle_items ?? [];
  const { lines, total: billed, prorated } = apportionLoggedBundle(items, event.amount);
  // What the breakdown's own rates come to, which is the figure the package was
  // discounted from where the two differ.
  const logged =
    Math.round(
      items.reduce((sum, item) => sum + item.quantity * Number(item.amount ?? 0), 0) * 100,
    ) / 100;

  const delivered = Number(event.quantity ?? 1);

  return (
    <TableRow className="hover:bg-transparent">
      <TableCell colSpan={colSpan} className="bg-muted/30 p-0">
        <div className="px-12 py-3">
          <p className="mb-2 text-xs font-medium text-muted-foreground">
            In “{event.bundle_name ?? "this bundle"}”
            {delivered > 1 && ` · ${delivered} delivered, figures are for one`}
          </p>

          <table className="w-full max-w-2xl text-sm">
            <thead>
              <tr className="text-xs text-muted-foreground">
                <th className="pb-1 text-left font-normal">Service</th>
                <th className="pb-1 text-right font-normal">Qty</th>
                <th className="pb-1 text-right font-normal">Rate</th>
                <th className="pb-1 text-right font-normal">Amount</th>
              </tr>
            </thead>
            <tbody>
              {lines.map((line, index) => (
                <tr key={`${line.service_id ?? "unnamed"}-${index}`} className="border-t">
                  <td className="py-1.5 pr-3">{line.service_name ?? "Unnamed service"}</td>
                  <td className="py-1.5 text-right tabular-nums">{line.quantity}</td>
                  <td className="py-1.5 text-right tabular-nums">{money(line.unit_amount)}</td>
                  <td className="py-1.5 text-right font-medium tabular-nums">
                    {money(line.quantity * line.unit_amount)}
                  </td>
                </tr>
              ))}
              <tr className="border-t">
                <td colSpan={3} className="py-1.5 pr-3 text-right text-xs text-muted-foreground">
                  Bundle total
                </td>
                <td className="py-1.5 text-right font-semibold tabular-nums">{money(billed)}</td>
              </tr>
            </tbody>
          </table>

          {prorated && (
            <p className="mt-2 max-w-2xl text-xs text-muted-foreground">
              Logged at {money(billed)} against {money(logged)} at the services' own rates — the
              difference is spread across the services above, so the invoice adds up to the price
              that was agreed.
            </p>
          )}
        </div>
      </TableCell>
    </TableRow>
  );
}

/**
 * The register, read whole, for an import that has to turn names into ids.
 *
 * Not the read a picker does — a picker asks the API for the companies matching
 * what is being typed, and this page's own company filter still does exactly
 * that. This is the other job: the import template's Company column is a
 * dropdown of every company that exists, and a file's rows name companies that
 * could be any of them, so the whole list is the answer and matching one row at
 * a time would be a request per line. The VIRRGIN statement import reads the
 * register for the same reason.
 *
 * Deliberately not a `useQuery`. It is called when the template is downloaded
 * and again when a file is parsed, and both have to see the register as it
 * stands at that moment: a template built from a cached list is a dropdown
 * offering companies that have since been struck off and missing the ones
 * incorporated this morning. The point of generating the file on every click is
 * lost if the list behind it is held anywhere.
 */
async function fetchCompanyIndex(): Promise<CompanyIndex> {
  const { data, error } = await api.companies
    .list()
    .all()
    .select(COMPANY_IDENTITY_COLUMNS)
    .order("legal_name");
  if (error) throw new Error(error.message);
  return buildCompanyIndex((data ?? []) as CompanyIdentity[]);
}

/** The catalogue, for the Service dropdown. Active only — an inactive service is not loggable. */
async function fetchServiceOptions(): Promise<{ id: string; name: string; code: string | null }[]> {
  const { data, error } = await api.services
    .list()
    .eq("active", true)
    .all()
    .select("id, name, code")
    .order("name");
  if (error) throw new Error(error.message);
  return (data ?? []) as { id: string; name: string; code: string | null }[];
}

/** The known types plus the ones already in use, which is what the log dialog offers. */
async function fetchExpenseTypes(): Promise<string[]> {
  const { data, error } = await api.workLog.expenses.list().all().select("expense_type");
  if (error) throw new Error(error.message);
  const inUse = ((data ?? []) as { expense_type?: string | null }[])
    .map((row) => (row.expense_type ?? "").trim())
    .filter(Boolean);
  return [...new Set([...EXPENSE_TYPES, ...inUse])].sort((a, b) => a.localeCompare(b));
}

/**
 * The labels an export writes for the ids it holds — the same string the
 * template's dropdown offers, so a file that comes out of the export is a file
 * the import accepts without anything being retyped.
 */
async function fetchCompanyLabels(
  ids: readonly (string | null | undefined)[],
): Promise<Record<string, string>> {
  const wanted = [...new Set(ids.filter((id): id is string => !!id))];
  if (wanted.length === 0) return {};
  const { data, error } = await api.companies
    .list()
    .in("id", wanted)
    .all()
    .select(COMPANY_IDENTITY_COLUMNS);
  if (error) throw new Error(error.message);
  return Object.fromEntries(
    ((data ?? []) as CompanyIdentity[]).map((company) => [company.id, companyLabel(company)]),
  );
}

function WorkLogPage() {
  const { canEdit } = useAuth();
  const qc = useQueryClient();
  const confirmDelete = useConfirmDelete();
  const [search, setSearch] = useState("");
  // Each picker's id *and* the name it chose. The id is what the filter sends;
  // the name is what the export dialog has to print to say which set a file
  // holds, and asking for it again by id would be a request for a string the
  // picker had in its hand a moment ago.
  const [company, setCompany] = useState<Picked>(NO_PICK);
  const [group, setGroup] = useState<Picked>(NO_PICK);
  const [service, setService] = useState<Picked>(NO_PICK);
  const [bundle, setBundle] = useState<Picked>(NO_PICK);
  const [expenseType, setExpenseType] = useState("all");
  // One range for both tabs, applied to whichever date column the tab keeps its
  // dates in. "What happened in October" is one question, not two — but it is
  // asked of two different columns, so the control says which; see
  // `dateFilterLabel`.
  const [dates, setDates] = useState<DateRange>(EMPTY_DATE_RANGE);
  const [billedFilter, setBilledFilter] = useState<string>("all");
  const [tab, setTab] = useState("services");
  const [logDialog, setLogDialog] = useState(false);
  const [exportTab, setExportTab] = useState<"services" | "expenses" | null>(null);
  const eventSort = useListSort({ column: "event_date", ascending: false });
  const expenseSort = useListSort({ column: "expense_date", ascending: false });
  // Held back so a search is one request when typing stops, not one per key.
  const query = useDebounced(search.trim());
  /**
   * The date range is debounced for the same reason the search box is.
   * An `<input type="date">` fires a change the moment its three segments are
   * all filled, so typing the year of "2026-03-01" walks through 0002, 0020 and
   * 0202 on the way — three complete, wrong dates, and three requests — before
   * it reaches the one that was meant.
   */
  const dateRange = useDebounced(dates);

  /** The types any expense actually carries, which is what the filter offers. */
  const expenseTypesInUse = useExpenseTypesInUse(tab === "expenses");

  /**
   * The filters both tabs share, applied to whichever list is being built.
   *
   * `company_id` is a GSI partition on both tables, so narrowing to one company
   * is an index query rather than a scan of the whole log — which is the reason
   * these filters belong in the API and not in a `useMemo` here. The date column
   * differs between the two tables, so it is named by the caller; ISO dates are
   * stored as text and compare in order, so the bounds are the dates themselves
   * and both are inclusive.
   */
  const shared = <T,>(list: ListBuilder<T>, dateColumn: string) => {
    if (company.id) list.eq("company_id", company.id);
    if (billedFilter === "unbilled") list.is("invoice_id", null);
    else if (billedFilter === "billed") list.not("invoice_id", "is", null);
    if (dateRange.from) list.gte(dateColumn, dateRange.from);
    if (dateRange.to) list.lte(dateColumn, dateRange.to);
    return list;
  };

  /**
   * Each tab's list, as the filters describe it — paged by the table, walked
   * whole by the export.
   *
   * The client group and the service go to the endpoint as parameters of its
   * own rather than as column filters, because neither question can be asked of
   * one row on its own: a log row names a company and the company names the
   * group, and a bundle logged grouped keeps what it covered in `bundle_items`
   * with no `service_id` at all. Both are resolved server-side — see
   * `WorkLogFilters` and `listEvents` in the API.
   */
  const eventList = () => {
    const list = shared(
      api.workLog.events.list({
        clientGroupId: group.id || undefined,
        coversServiceId: service.id || undefined,
      }),
      "event_date",
    );
    if (bundle.id) list.eq("bundle_id", bundle.id);
    if (query) {
      list.or([
        { column: "description", op: "ilike", value: `%${query}%` },
        { column: "notes", op: "ilike", value: `%${query}%` },
        // The bundle's name is on the row, so "incorporation" finds the entries
        // logged from that bundle as well as the ones describing it.
        { column: "bundle_name", op: "ilike", value: `%${query}%` },
      ]);
    }
    return list.order(eventSort.sort.column, { ascending: eventSort.sort.ascending });
  };
  const expenseList = () => {
    const list = shared(
      api.workLog.expenses.list({ clientGroupId: group.id || undefined }),
      "expense_date",
    );
    if (expenseType !== "all") list.eq("expense_type", expenseType);
    if (query) {
      list.or([
        { column: "description", op: "ilike", value: `%${query}%` },
        { column: "expense_type", op: "ilike", value: `%${query}%` },
        { column: "notes", op: "ilike", value: `%${query}%` },
      ]);
    }
    return list.order(expenseSort.sort.column, { ascending: expenseSort.sort.ascending });
  };

  // One pager per tab, because the two tabs are two lists: paging the expenses
  // should not move where you were in the services. The shared filters reset
  // both, since they change what both lists contain.
  const sharedFilters = {
    query,
    companyId: company.id,
    groupId: group.id,
    billedFilter,
    from: dateRange.from,
    to: dateRange.to,
  };
  const eventFilters = { ...sharedFilters, serviceId: service.id, bundleId: bundle.id };
  const expenseFilters = { ...sharedFilters, expenseType };
  const eventPager = usePagination({ resetKey: { ...eventFilters, sort: eventSort.sort } });
  const expensePager = usePagination({ resetKey: { ...expenseFilters, sort: expenseSort.sort } });

  const { data: eventPage, isLoading: eventsLoading } = useQuery({
    queryKey: [
      "service_events",
      {
        ...eventFilters,
        sort: eventSort.sort,
        page: eventPager.page,
        pageSize: eventPager.pageSize,
      },
    ],
    queryFn: async () => {
      const { data, error, count } = await eventList().page(eventPager.page, eventPager.pageSize);
      if (error) throw error;
      return { rows: (data ?? []) as ServiceEvent[], total: count ?? 0 };
    },
    placeholderData: (previous) => previous,
  });
  const { data: expensePage, isLoading: expensesLoading } = useQuery({
    queryKey: [
      "sundry_expenses",
      {
        ...expenseFilters,
        sort: expenseSort.sort,
        page: expensePager.page,
        pageSize: expensePager.pageSize,
      },
    ],
    queryFn: async () => {
      const { data, error, count } = await expenseList().page(
        expensePager.page,
        expensePager.pageSize,
      );
      if (error) throw error;
      return { rows: (data ?? []) as SundryExpense[], total: count ?? 0 };
    },
    placeholderData: (previous) => previous,
  });

  /**
   * The active filters in words, per tab.
   *
   * This is what the export dialog prints next to "filtered records", and what
   * goes into the PDF's header: the file does not otherwise say which set it
   * holds, and "filtered" on its own does not say which filters. Null when
   * nothing is narrowed, which is also what tells the dialog that "filtered" and
   * "the whole log" are the same set and which of the two to offer first.
   */
  const sharedSummary = (dateLabel: string) => [
    query ? `matching \u201c${query}\u201d` : null,
    company.id ? `for ${company.label || "one company"}` : null,
    group.id ? `in ${group.label || "one client group"}` : null,
    billedFilter === "unbilled"
      ? "not yet billed"
      : billedFilter === "billed"
        ? "already billed"
        : null,
    // Named after the column it reads, the same way the control is: a file
    // headed "dated October" does not say which of a row's dates that was.
    describeDateRange(dateLabel, dateRange),
  ];
  const eventSummary =
    [
      ...sharedSummary("delivered"),
      service.id ? `delivering ${service.label || "one service"}` : null,
      bundle.id ? `logged from ${bundle.label || "one bundle"}` : null,
    ]
      .filter(Boolean)
      .join(", ") || null;
  const expenseSummary =
    [...sharedSummary("incurred"), expenseType !== "all" ? `of type ${expenseType}` : null]
      .filter(Boolean)
      .join(", ") || null;

  /** The date column the open tab is reading, in the words the control shows. */
  const dateFilterLabel = tab === "services" ? "Service date" : "Expense date";

  const narrowed = Boolean(
    query ||
    company.id ||
    group.id ||
    service.id ||
    bundle.id ||
    billedFilter !== "all" ||
    expenseType !== "all" ||
    dates.from ||
    dates.to,
  );
  const clearFilters = () => {
    setSearch("");
    setCompany(NO_PICK);
    setGroup(NO_PICK);
    setService(NO_PICK);
    setBundle(NO_PICK);
    setExpenseType("all");
    setDates(EMPTY_DATE_RANGE);
    setBilledFilter("all");
  };

  /**
   * How many rows each table holds in total, for the export dialog's "entire
   * log" option.
   *
   * A scope offered without its count is not an informed choice — the whole
   * point of asking is that the operator can see that "filtered" is twelve rows
   * and "everything" is nine thousand before deciding which file to write.
   */
  const { data: totals } = useQuery({
    queryKey: ["work_log_totals"],
    queryFn: async () => {
      const [loggedServices, loggedExpenses] = await Promise.all([
        api.workLog.events.list().countOnly(),
        api.workLog.expenses.list().countOnly(),
      ]);
      return { events: loggedServices.count ?? 0, expenses: loggedExpenses.count ?? 0 };
    },
  });

  const events = eventPage?.rows ?? [];
  // Both tables have the same shape: eight data columns, plus the checkbox and
  // the actions columns that only an editor has anything to put in.
  const columnCount = canEdit ? 10 : 8;
  const expenses = expensePage?.rows ?? [];

  /**
   * How much of each tab is still waiting to be invoiced — within the filters.
   *
   * Counted by the API rather than off the rows on screen, which was only ever
   * right while the page held every row.
   *
   * It followed the whole log and ignored the filters until the filter bar grew
   * past a company picker. A number beside a tab is read as describing what the
   * tab is showing, and narrowing to one client and asking how much of their
   * work is still to bill is the gesture this page exists for — so it is the
   * filtered set that is counted, and the badge moves with the bar.
   *
   * Built from the same list the table pages, with the unbilled filter added, so
   * the badge and the footer cannot come to describe different sets. Asking for
   * "billed only" leaves nothing unbilled to count, and the badge says nought
   * rather than guessing that the filter was not meant.
   *
   * Keyed on the filters alone and not on the sort: re-ordering a list does not
   * change how many rows are in it.
   */
  const { data: unbilledEvents } = useQuery({
    queryKey: ["work_log_unbilled", "events", eventFilters],
    queryFn: async () => (await eventList().is("invoice_id", null).countOnly()).count ?? 0,
    // The last count stays on the badge while the next one loads, so it does not
    // blink away and back on every keystroke in the search box.
    placeholderData: (previous) => previous,
  });
  const { data: unbilledExpenses } = useQuery({
    queryKey: ["work_log_unbilled", "expenses", expenseFilters],
    queryFn: async () => (await expenseList().is("invoice_id", null).countOnly()).count ?? 0,
    placeholderData: (previous) => previous,
  });

  /**
   * The names the two tables print, for the ids on their own pages.
   *
   * Both lists used to be read whole on load so that a `find()` here could name
   * every row. They are read by id instead now, which is what lets the pickers
   * below search the API rather than filter a downloaded table.
   */
  const companyName = useEntityNames("companies", [
    ...events.map((e) => e.company_id),
    ...expenses.map((e) => e.company_id),
  ]);
  const serviceName = useEntityNames(
    "services",
    events.map((e) => e.service_id),
  );
  /**
   * The invoices behind the billed rows on screen — number, date and status.
   *
   * Resolved for the ids this page actually shows, not by reading the register
   * whole: a lookup of every invoice ever raised is capped at one page, so the
   * oldest billed rows would eventually show a stamp with no number beside it.
   * Both tabs feed one hook, so a page whose work and expenses went onto the
   * same invoice asks for it once.
   */
  const invoiceStamp = useInvoiceStamps([
    ...events.map((e) => e.invoice_id),
    ...expenses.map((e) => e.invoice_id),
  ]);

  const evSel = useRowSelection(events);
  const exSel = useRowSelection(expenses);

  /**
   * Which bundled entries are opened out to show what is in them.
   *
   * By id rather than by index, so paging or re-sorting cannot leave a different
   * row open than the one that was clicked.
   */
  const [openBundles, setOpenBundles] = useState<Set<string>>(new Set());
  const toggleBundle = (id: string) =>
    setOpenBundles((open) => {
      const next = new Set(open);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const refreshEvents = () => {
    qc.invalidateQueries({ queryKey: ["service_events"] });
    qc.invalidateQueries({ queryKey: ["work_log_unbilled"] });
    qc.invalidateQueries({ queryKey: ["work_log_totals"] });
  };
  const refreshExpenses = () => {
    qc.invalidateQueries({ queryKey: ["sundry_expenses"] });
    qc.invalidateQueries({ queryKey: ["work_log_unbilled"] });
    qc.invalidateQueries({ queryKey: ["work_log_totals"] });
    // A new kind of cost changes what the type filter can offer.
    qc.invalidateQueries({ queryKey: ["expense_types_in_use"] });
  };

  /**
   * The rows behind whichever file was asked for, on either tab.
   *
   * `collect()` and not `all()`: a list endpoint answers with one page, and
   * `all()` asks for one page of a thousand — which is the right read for a
   * dropdown and the wrong one for a log that only grows, because past a
   * thousand rows the file is quietly short and a spreadsheet does not say
   * where it stopped. The walk reports when it hits its own ceiling instead, and
   * the dialog says so out loud.
   *
   * "Entire log" builds its list from nothing rather than from `eventList()`
   * with the filters skipped, so there is no filter that can be forgotten here
   * and silently narrow the file that claims to hold everything. The sort is
   * repeated because it is the file's order, not the table's.
   */
  const eventExportRows = async (scope: ExportScope): Promise<ExportRowSet> => {
    const list =
      scope === "filtered"
        ? eventList()
        : api.workLog.events.list().order("event_date", { ascending: false });
    const { data, error, truncated } = await list.collect();
    if (error) throw new Error(error.message);
    const rows = (data ?? []) as ServiceEvent[];

    // The export's own id sets, which are wider than the page on screen — so it
    // resolves its own names and invoices rather than reading the table's
    // lookups, which only hold the current page.
    //
    // Companies are resolved to the label the import template's dropdown offers
    // — name and registry number — rather than to the bare legal name the table
    // prints. That is what makes this file re-importable: the bare name of one
    // of two same-named companies identifies neither.
    const [companyNames, serviceNames, stamps] = await Promise.all([
      fetchCompanyLabels(rows.map((e) => e.company_id)),
      fetchEntityNames(
        "services",
        rows.map((e) => e.service_id),
      ),
      fetchInvoiceStamps(rows.map((e) => e.invoice_id)),
    ]);

    // Every column a grouped entry shares with the services inside it. The three
    // that differ — Service, Qty and Amount — are what the split fills in.
    const written = rows.flatMap((e) => {
      const invoice = invoiceStampCells(e.invoice_id ? (stamps[e.invoice_id] ?? null) : null);
      return serviceEventExportRows(
        e,
        {
          [SERVICE_EVENT_HEADERS.event_date]: e.event_date,
          [SERVICE_EVENT_HEADERS.company]: companyNames[e.company_id] ?? "—",
          [SERVICE_EVENT_HEADERS.description]: e.description ?? "",
          [SERVICE_EVENT_HEADERS.notes]: e.notes ?? "",
          [SERVICE_EVENT_EXPORT_HEADERS.bundle]: e.bundle_name ?? "",
          // Still the shape it was *logged* in, which is what the column asks.
          // The file showing a package itemised does not change how it was keyed
          // in, and the rows of one package are told apart by their Service.
          [SERVICE_EVENT_EXPORT_HEADERS.bundled_as]: bundleKindLabel(e),
          [SERVICE_EVENT_EXPORT_HEADERS.billed]: e.invoice_id ? "Yes" : "No",
          [SERVICE_EVENT_EXPORT_HEADERS.invoice]: invoice.number,
          [SERVICE_EVENT_EXPORT_HEADERS.invoice_date]: invoice.date,
          [SERVICE_EVENT_EXPORT_HEADERS.invoice_status]: invoice.status,
        },
        serviceNames,
      );
    });

    return {
      truncated,
      rows: written,
      // Counted in both when they differ, because the dialog offered the choice
      // in entries and the file holds rows: "11" against a file of 18 lines is
      // the kind of mismatch that reads as a broken export.
      summary:
        written.length === rows.length
          ? undefined
          : `${rows.length.toLocaleString()} logged services as ${written.length.toLocaleString()} rows, bundles itemised`,
    };
  };

  const expenseExportRows = async (scope: ExportScope): Promise<ExportRowSet> => {
    const list =
      scope === "filtered"
        ? expenseList()
        : api.workLog.expenses.list().order("expense_date", { ascending: false });
    const { data, error, truncated } = await list.collect();
    if (error) throw new Error(error.message);
    const rows = (data ?? []) as SundryExpense[];

    const [companyNames, stamps] = await Promise.all([
      fetchCompanyLabels(rows.map((e) => e.company_id)),
      fetchInvoiceStamps(rows.map((e) => e.invoice_id)),
    ]);

    return {
      truncated,
      rows: rows.map((e) => {
        const invoice = invoiceStampCells(e.invoice_id ? (stamps[e.invoice_id] ?? null) : null);
        return {
          [EXPENSE_HEADERS.expense_date]: e.expense_date,
          [EXPENSE_HEADERS.company]: companyNames[e.company_id] ?? "—",
          [EXPENSE_HEADERS.expense_type]: e.expense_type,
          [EXPENSE_HEADERS.description]: e.description,
          [EXPENSE_HEADERS.amount]: e.amount,
          [EXPENSE_HEADERS.markup_pct]: e.markup_pct,
          [EXPENSE_HEADERS.notes]: e.notes ?? "",
          [EXPENSE_EXPORT_HEADERS.recovered]: e.invoice_id ? "Yes" : "No",
          [EXPENSE_EXPORT_HEADERS.invoice]: invoice.number,
          [EXPENSE_EXPORT_HEADERS.invoice_date]: invoice.date,
          [EXPENSE_EXPORT_HEADERS.invoice_status]: invoice.status,
        };
      }),
    };
  };

  const removeEvent = async (id: string) => {
    if (!(await confirmDelete("this logged service"))) return;
    const { error } = await api.workLog.events.remove(id);
    if (error) return notify.error(error);
    refreshEvents();
  };
  const removeExpense = async (id: string) => {
    if (!(await confirmDelete("this expense"))) return;
    const { error } = await api.workLog.expenses.remove(id);
    if (error) return notify.error(error);
    refreshExpenses();
  };

  return (
    <ListPage>
      <Tabs value={tab} onValueChange={setTab} className="flex min-h-0 flex-1 flex-col">
        <ListPageHeader>
          <PageHeader
            title="Work & Expense Log"
            description="Services delivered and costs incurred. Anything unbilled shows up on the next invoice."
            actions={
              canEdit && (
                <Button onClick={() => setLogDialog(true)}>
                  <Plus className="mr-2 h-4 w-4" /> New log entry
                </Button>
              )
            }
          />

          {/* The tab picks which log is listed, so it leads; the search is the
            same for both and sits beside it; export and import act on the open
            tab. The filters that narrow it come underneath. */}
          <div className="mb-3 flex flex-wrap items-center gap-2">
            <TabsList className="shrink-0">
              <TabsTrigger value="services" className="gap-2">
                Services delivered
                <UnbilledCount count={unbilledEvents ?? 0} unit="service" showZero={narrowed} />
              </TabsTrigger>
              <TabsTrigger value="expenses" className="gap-2">
                Sundry expenses
                <UnbilledCount count={unbilledExpenses ?? 0} unit="expense" showZero={narrowed} />
              </TabsTrigger>
            </TabsList>
            <ListSearch
              value={search}
              onChange={setSearch}
              placeholder="Search description, type or notes…"
            />
            <div className="flex gap-2">
              {tab === "services" ? (
                <>
                  <Button variant="outline" onClick={() => setExportTab("services")}>
                    <Download className="mr-2 h-4 w-4" /> Export
                  </Button>
                  {canEdit && (
                    <ImportButton
                      table="service_events"
                      entityLabel="logged service"
                      entityLabelPlural="logged services"
                      template={{
                        filename: "somasi-services-delivered-template.xlsx",
                        sheetName: "Services delivered",
                        // A function, so the file is built from the register as it
                        // stands on the click. Both dropdowns are lists of records,
                        // and records change — that is the whole reason the template
                        // is generated rather than kept.
                        columns: async () =>
                          serviceEventTemplateColumns(
                            await fetchCompanyIndex(),
                            await fetchServiceOptions(),
                          ),
                      }}
                      hint="Company and Service are dropdowns of what actually exists — pick, do not type. Date and Company are all a row needs; leave Amount blank to let the fee schedule price it."
                      validate={async (parsed) =>
                        validateServiceEventRows(
                          parsed,
                          await fetchCompanyIndex(),
                          await fetchServiceOptions(),
                        )
                      }
                      onImported={refreshEvents}
                    />
                  )}
                </>
              ) : (
                <>
                  <Button variant="outline" onClick={() => setExportTab("expenses")}>
                    <Download className="mr-2 h-4 w-4" /> Export
                  </Button>
                  {canEdit && (
                    <ImportButton
                      table="sundry_expenses"
                      entityLabel="expense"
                      entityLabelPlural="expenses"
                      template={{
                        filename: "somasi-sundry-expenses-template.xlsx",
                        sheetName: "Sundry expenses",
                        columns: async () =>
                          expenseTemplateColumns(
                            await fetchCompanyIndex(),
                            await fetchExpenseTypes(),
                          ),
                      }}
                      hint="Company and Type are dropdowns of what actually exists — pick, do not type. Date, Company, Type, Description and Amount are all required."
                      validate={async (parsed) =>
                        validateExpenseRows(
                          parsed,
                          await fetchCompanyIndex(),
                          await fetchExpenseTypes(),
                        )
                      }
                      onImported={refreshExpenses}
                    />
                  )}
                </>
              )}
            </div>
          </div>

          <ListToolbar>
            {/* The group comes before the company it narrows, reading order being
              the order the two are usually picked in. */}
            <ClientGroupSelect
              value={group.id}
              {...pickHandlers((picked) => {
                setGroup(picked);
                // A company outside the newly chosen group would AND with it to
                // nothing, and a filter bar that silently describes an empty set
                // is worse than one that drops the narrower choice.
                setCompany(NO_PICK);
              })}
              clearable
              clearLabel="All client groups"
              placeholder="All client groups"
              className="w-48"
              contentClassName="w-72 p-0"
            />

            {/* A picker rather than free text: the company's name is not stored on a
            log row — it is resolved by id — so there is nothing on the row for
            the API to match a typed name against. */}
            <CompanySelect
              value={company.id}
              {...pickHandlers(setCompany)}
              filters={group.id ? { clientGroupId: group.id } : {}}
              placeholder="All companies"
              clearable
              clearLabel="All companies"
              showClientGroup={!group.id}
              className="w-52"
              contentClassName="w-72 p-0"
            />

            {/* One control for both tabs, but it reads a different column on each —
              `event_date`, the day the service was delivered, and `expense_date`,
              the day the cost was incurred. So it is named after the column it is
              actually reading: "Dated" alone left you to guess which of the two
              dates on a row it meant, and a log row has only one but the page
              has two. */}
            <DateRangeFilter
              label={dateFilterLabel}
              value={dates}
              onChange={setDates}
              className="w-auto"
            />

            {/* Named rather than left as a bare "All": its neighbours read "All
              companies", "All services" and so on, and a bare "All" would read as
              another of them with its noun missing. */}
            <FilterSelect
              value={billedFilter}
              onChange={setBilledFilter}
              options={[
                { value: "all", label: "Billed & unbilled" },
                { value: "unbilled", label: "Unbilled only" },
                { value: "billed", label: "Billed only" },
              ]}
            />

            {/* The last two filters belong to one tab each, so they are shown with
              that tab rather than kept on screen asking a question the list in
              front of you cannot answer. */}
            {tab === "services" ? (
              <>
                {/* Matches the entries that delivered this service *inside* a
                  bundle as well as the ones that name it on their own — a bundle
                  logged grouped has no `service_id`, and leaving those out would
                  under-report the very work that was packaged. */}
                <ServiceSelect
                  value={service.id}
                  {...pickHandlers(setService)}
                  placeholder="All services"
                  clearable
                  clearLabel="All services"
                  className="w-48"
                  contentClassName="w-80 p-0"
                />
                <ServiceBundleSelect
                  value={bundle.id}
                  {...pickHandlers(setBundle)}
                  placeholder="All bundles"
                  clearable
                  clearLabel="All bundles"
                  className="w-48"
                  contentClassName="w-80 p-0"
                />
              </>
            ) : (
              <SearchableSelect
                value={expenseType}
                onChange={setExpenseType}
                options={[
                  { value: "all", label: "All expense types" },
                  ...expenseTypesInUse.map((type) => ({ value: type, label: type })),
                ]}
                searchPlaceholder="Search expense types…"
                emptyText="No expense type matches that."
                className="w-48"
                contentClassName="w-64 p-0"
              />
            )}

            {narrowed && (
              <Button variant="ghost" size="sm" onClick={clearFilters}>
                <X className="mr-1 h-4 w-4" /> Clear filters
              </Button>
            )}
          </ListToolbar>
        </ListPageHeader>

        {/* The height has to be handed down through the tabs: `Tabs` and whichever
          panel is open are both filling columns, so the card at the bottom of
          the chain has something left to take.

          The panels are `hidden` by default and `flex` only when active, rather
          than `flex` with the inactive one hidden. Radix hides a closed panel
          with the `hidden` attribute, and the UA rule behind that attribute
          loses to any class — so a panel carrying `flex` stays visible and both
          tabs render at once. */}

        <TabsContent
          value="services"
          className="mt-0 hidden min-h-0 flex-1 flex-col gap-3 data-[state=active]:flex"
        >
          {canEdit && (
            <BulkBar
              sel={evSel}
              resource={api.workLog.events}
              entityLabel="logged service"
              onDone={refreshEvents}
            />
          )}
          <ListPageTable>
            <Table containerClassName="min-h-0 flex-1">
              <TableHeader sticky>
                <TableRow>
                  {canEdit && (
                    <TableHead className="w-10">
                      <SelectAllCheckbox sel={evSel} />
                    </TableHead>
                  )}
                  <SortHeader sort={eventSort} column="event_date">
                    Date
                  </SortHeader>
                  <TableHead>Company</TableHead>
                  <TableHead>Service</TableHead>
                  <SortHeader sort={eventSort} column="description">
                    Description
                  </SortHeader>
                  <SortHeader
                    sort={eventSort}
                    column="quantity"
                    className="text-right"
                    align="right"
                  >
                    Qty
                  </SortHeader>
                  <SortHeader sort={eventSort} column="amount" className="text-right" align="right">
                    Amount
                  </SortHeader>
                  {/* Narrow on purpose: notes are the long tail of the row and
                      the columns that bill come first. `Truncated` gives the
                      whole note back on hover, so the width costs nothing. */}
                  <TableHead className="w-32">Notes</TableHead>
                  <TableHead>Status</TableHead>
                  {canEdit && (
                    <TableHead className="w-px whitespace-nowrap text-right">Actions</TableHead>
                  )}
                </TableRow>
              </TableHeader>
              <TableBody>
                {eventsLoading ? (
                  <TableMessageRow colSpan={columnCount}>Loading…</TableMessageRow>
                ) : events.length === 0 ? (
                  <TableMessageRow colSpan={columnCount}>
                    {narrowed
                      ? "No logged services match these filters."
                      : "No services logged yet."}
                  </TableMessageRow>
                ) : (
                  events.map((e) => (
                    <Fragment key={e.id}>
                      <TableRow data-state={evSel.isSelected(e.id) ? "selected" : undefined}>
                        {canEdit && (
                          <TableCell>
                            <RowCheckbox sel={evSel} id={e.id} />
                          </TableCell>
                        )}
                        <TableCell className="whitespace-nowrap tabular-nums">
                          {e.event_date}
                        </TableCell>
                        <TableCell className="max-w-48 font-medium">
                          <Truncated text={companyName(e.company_id)} />
                        </TableCell>
                        <TableCell>
                          <LoggedServiceCell
                            event={e}
                            serviceName={serviceName(e.service_id)}
                            open={openBundles.has(e.id)}
                            onToggle={() => toggleBundle(e.id)}
                          />
                        </TableCell>
                        <TableCell className="max-w-xs text-sm text-muted-foreground">
                          <Truncated text={e.description} />
                        </TableCell>
                        <TableCell className="text-right">{e.quantity}</TableCell>
                        <TableCell className="whitespace-nowrap text-right tabular-nums">
                          {e.amount != null ? `USD ${Number(e.amount).toFixed(2)}` : "—"}
                        </TableCell>
                        <TableCell className="max-w-32 text-sm text-muted-foreground">
                          <Truncated text={e.notes} />
                        </TableCell>
                        <TableCell>
                          <BilledCell
                            invoiceId={e.invoice_id}
                            stamp={invoiceStamp(e.invoice_id)}
                            billed="Billed"
                            unbilled="Unbilled"
                          />
                        </TableCell>
                        {canEdit && (
                          <TableCell>
                            {!e.invoice_id && (
                              <RowActions>
                                <RowAction
                                  label="Delete"
                                  destructive
                                  onClick={() => removeEvent(e.id)}
                                >
                                  <Trash2 />
                                </RowAction>
                              </RowActions>
                            )}
                          </TableCell>
                        )}
                      </TableRow>

                      {/* Asked again here, not just when the toggle was drawn: a
                          refetch can leave a row open whose breakdown has gone,
                          and an empty table under a row is worse than none. */}
                      {openBundles.has(e.id) && (e.bundle_items?.length ?? 0) > 0 && (
                        <BundleBreakdownRow event={e} colSpan={columnCount} />
                      )}
                    </Fragment>
                  ))
                )}
              </TableBody>
            </Table>
            <TablePagination
              pagination={eventPager}
              total={eventPage?.total}
              loading={eventsLoading}
              unit="logged service"
            />
          </ListPageTable>
        </TabsContent>

        <TabsContent
          value="expenses"
          className="mt-0 hidden min-h-0 flex-1 flex-col gap-3 data-[state=active]:flex"
        >
          {canEdit && (
            <BulkBar
              sel={exSel}
              resource={api.workLog.expenses}
              entityLabel="expense"
              onDone={refreshExpenses}
            />
          )}
          <ListPageTable>
            <Table containerClassName="min-h-0 flex-1">
              <TableHeader sticky>
                <TableRow>
                  {canEdit && (
                    <TableHead className="w-10">
                      <SelectAllCheckbox sel={exSel} />
                    </TableHead>
                  )}
                  <SortHeader sort={expenseSort} column="expense_date">
                    Date
                  </SortHeader>
                  <TableHead>Company</TableHead>
                  <SortHeader sort={expenseSort} column="expense_type">
                    Type
                  </SortHeader>
                  <SortHeader sort={expenseSort} column="description">
                    Description
                  </SortHeader>
                  <SortHeader
                    sort={expenseSort}
                    column="amount"
                    className="text-right"
                    align="right"
                  >
                    Amount
                  </SortHeader>
                  <SortHeader
                    sort={expenseSort}
                    column="markup_pct"
                    className="text-right"
                    align="right"
                  >
                    Markup
                  </SortHeader>
                  <TableHead className="w-32">Notes</TableHead>
                  <TableHead>Status</TableHead>
                  {canEdit && (
                    <TableHead className="w-px whitespace-nowrap text-right">Actions</TableHead>
                  )}
                </TableRow>
              </TableHeader>
              <TableBody>
                {expensesLoading ? (
                  <TableMessageRow colSpan={columnCount}>Loading…</TableMessageRow>
                ) : expenses.length === 0 ? (
                  <TableMessageRow colSpan={columnCount}>
                    {narrowed ? "No expenses match these filters." : "No expenses logged yet."}
                  </TableMessageRow>
                ) : (
                  expenses.map((e) => (
                    <TableRow
                      key={e.id}
                      data-state={exSel.isSelected(e.id) ? "selected" : undefined}
                    >
                      {canEdit && (
                        <TableCell>
                          <RowCheckbox sel={exSel} id={e.id} />
                        </TableCell>
                      )}
                      <TableCell className="whitespace-nowrap tabular-nums">
                        {e.expense_date}
                      </TableCell>
                      <TableCell className="max-w-48 font-medium">
                        <Truncated text={companyName(e.company_id)} />
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline">{e.expense_type}</Badge>
                      </TableCell>
                      <TableCell className="max-w-xs text-sm text-muted-foreground">
                        <Truncated text={e.description} />
                      </TableCell>
                      <TableCell className="whitespace-nowrap text-right tabular-nums">
                        USD {Number(e.amount).toFixed(2)}
                      </TableCell>
                      <TableCell className="text-right">
                        {Number(e.markup_pct) ? `${e.markup_pct}%` : "—"}
                      </TableCell>
                      <TableCell className="max-w-32 text-sm text-muted-foreground">
                        <Truncated text={e.notes} />
                      </TableCell>
                      <TableCell>
                        <BilledCell
                          invoiceId={e.invoice_id}
                          stamp={invoiceStamp(e.invoice_id)}
                          billed="Recovered"
                          unbilled="Unrecovered"
                        />
                      </TableCell>
                      {canEdit && (
                        <TableCell>
                          {!e.invoice_id && (
                            <RowActions>
                              <RowAction
                                label="Delete"
                                destructive
                                onClick={() => removeExpense(e.id)}
                              >
                                <Trash2 />
                              </RowAction>
                            </RowActions>
                          )}
                        </TableCell>
                      )}
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
            <TablePagination
              pagination={expensePager}
              total={expensePage?.total}
              loading={expensesLoading}
              unit="expense"
            />
          </ListPageTable>
        </TabsContent>
      </Tabs>

      {/* Mounted on opening rather than kept mounted and toggled, which is what
          lets the dialog read the filters as they are at that moment: its
          default scope is "filtered" only when something is filtered, and a
          dialog mounted with the page would have settled that question while the
          filter bar was still empty. */}
      {exportTab === "services" && (
        <ListExportDialog
          open
          onOpenChange={(open) => !open && setExportTab(null)}
          title="Export services delivered"
          description="Every column the log holds, with the invoice each billed entry went onto. A bundle logged as one line is itemised — one row per service in it, as the invoice bills it."
          filenameBase="somasi-services-delivered"
          reportTitle="Services delivered"
          unitPlural="logged services"
          entireLabel="Entire log"
          entireHint="Every service ever logged, ignoring the filters on the list."
          filteredCount={eventPage?.total}
          totalCount={totals?.events}
          filterSummary={eventSummary}
          fetchRows={eventExportRows}
          columns={SERVICE_EVENT_EXPORT_COLUMNS}
          pdfCols={[
            { header: "Date", dataKey: SERVICE_EVENT_HEADERS.event_date },
            { header: "Company", dataKey: SERVICE_EVENT_HEADERS.company },
            { header: "Service", dataKey: SERVICE_EVENT_HEADERS.service },
            { header: "Bundle", dataKey: SERVICE_EVENT_EXPORT_HEADERS.bundle },
            { header: "Qty", dataKey: SERVICE_EVENT_HEADERS.quantity },
            { header: "Amount", dataKey: SERVICE_EVENT_HEADERS.amount },
            { header: "Invoice", dataKey: SERVICE_EVENT_EXPORT_HEADERS.invoice },
            { header: "Invoiced", dataKey: SERVICE_EVENT_EXPORT_HEADERS.invoice_date },
            { header: "Status", dataKey: SERVICE_EVENT_EXPORT_HEADERS.invoice_status },
          ]}
        />
      )}

      {exportTab === "expenses" && (
        <ListExportDialog
          open
          onOpenChange={(open) => !open && setExportTab(null)}
          title="Export sundry expenses"
          description="Every column the log holds, with the invoice each recovered cost went onto."
          filenameBase="somasi-sundry-expenses"
          reportTitle="Sundry expenses"
          unitPlural="expenses"
          entireLabel="Entire log"
          entireHint="Every expense ever recorded, ignoring the filters on the list."
          filteredCount={expensePage?.total}
          totalCount={totals?.expenses}
          filterSummary={expenseSummary}
          fetchRows={expenseExportRows}
          columns={EXPENSE_EXPORT_COLUMNS}
          pdfCols={[
            { header: "Date", dataKey: EXPENSE_HEADERS.expense_date },
            { header: "Company", dataKey: EXPENSE_HEADERS.company },
            { header: "Type", dataKey: EXPENSE_HEADERS.expense_type },
            { header: "Description", dataKey: EXPENSE_HEADERS.description },
            { header: "Amount", dataKey: EXPENSE_HEADERS.amount },
            { header: "Invoice", dataKey: EXPENSE_EXPORT_HEADERS.invoice },
            { header: "Invoiced", dataKey: EXPENSE_EXPORT_HEADERS.invoice_date },
            { header: "Status", dataKey: EXPENSE_EXPORT_HEADERS.invoice_status },
          ]}
        />
      )}

      <LogEntryDialog
        open={logDialog}
        onOpenChange={setLogDialog}
        /* Opens on whichever kind you are already looking at — still a choice,
           just one that usually needs no click. */
        initialKind={tab === "expenses" ? "expense" : "service"}
        onSavedEvent={refreshEvents}
        onSavedExpense={refreshExpenses}
      />
    </ListPage>
  );
}

/**
 * How much of a tab's list is still waiting to be invoiced, within the filters.
 *
 * Counted by the API over the filtered set, not off the rows on screen: the page
 * holds twenty of them, and counting those would report twenty as the ceiling of
 * everything outstanding.
 *
 * Zero is shown while a filter is set and hidden while none is, which is two
 * different facts wearing the same number. Unfiltered, nought outstanding means
 * the log is settled and the tab should look it. Filtered, it is the answer to
 * the question just asked — "nothing of theirs is unbilled" — and hiding it
 * there would read as the badge having given up rather than having replied.
 */
function UnbilledCount({
  count,
  unit,
  showZero,
}: {
  count: number;
  unit: string;
  /** True while a filter is set, so a nought is an answer rather than silence. */
  showZero: boolean;
}) {
  if (count === 0 && !showZero) return null;
  return (
    <span
      className={cn(
        badgeVariants({ variant: "default" }),
        "px-1.5",
        count === 0
          ? // Not a warning when there is nothing to warn about: a filtered set
            // with nothing outstanding is good news in the same slot.
            "bg-muted text-muted-foreground"
          : "bg-warning text-warning-foreground",
      )}
      title={`${count} ${unit}${count === 1 ? "" : "s"} not yet on an invoice${
        showZero ? ", of those the filters match" : ""
      }`}
    >
      {count}
      <span className="sr-only">
        {" "}
        {unit}
        {count === 1 ? "" : "s"} unbilled
      </span>
    </span>
  );
}

type LogKind = "service" | "expense";

/**
 * One service of a bundled line, while it is being keyed in.
 *
 * Quantity and rate are strings because they are what is in two inputs — a rate
 * cleared to nothing is a service the package figure does not cover, which is a
 * different fact from a rate of zero and has to survive being typed.
 *
 * `origin` is the level the rate opened at. It is shown beside each service and
 * is the whole point of resolving the price list here — a bundle delivered to a
 * client on a negotiated schedule should arrive at that client's rates, not at
 * the catalogue's, and the operator should be able to see which they got.
 */
type BundleLineItem = {
  service_id: string;
  service_name: string;
  /** The bundle's invoice wording for the service; null prints the service name. */
  description: string | null;
  quantity: string;
  amount: string;
  /** Null when nothing published a rate for this service. */
  origin: BundleRateOrigin | null;
};

/**
 * The bundle a line stands for, and what the operator has done to its figures.
 *
 * The two flags are what keep an automatic reprice from overwriting an answer a
 * person gave. `rates_edited` is set the moment a service's rate or quantity in
 * the breakdown is typed over, and stops the price list from being re-read onto
 * it; `total_typed` is set when the package figure itself is typed over, and
 * stops it from following the breakdown's own total. Neither is stored — what is
 * saved is the figures they protect.
 */
type BundleLine = {
  id: string;
  name: string;
  items: BundleLineItem[];
  rates_edited: boolean;
  total_typed: boolean;
};

/**
 * A service line being keyed in; `key` is React's identity, not the row's.
 *
 * `bundle` is set on a line that is a whole bundle delivered — `items` holds the
 * services the one figure covers, each at the rate this client is priced at. A
 * line with no `bundle` is work keyed in by hand.
 */
type ServiceLine = {
  key: string;
  service_id: string;
  quantity: string;
  amount: string;
  description: string;
  bundle: BundleLine | null;
};

/** An expense line being keyed in. */
type ExpenseLine = {
  key: string;
  expense_type: string;
  description: string;
  amount: string;
  markup_pct: string;
};

let lineSeq = 0;
const nextKey = () => `line-${++lineSeq}`;

const blankServiceLine = (): ServiceLine => ({
  key: nextKey(),
  service_id: "",
  quantity: "1",
  amount: "",
  description: "",
  bundle: null,
});

/** This client's rate for each service, keyed by service — the price list, indexed. */
type RateMap = Map<string, ResolvedService>;

const round2 = (value: number) => Math.round(value * 100) / 100;

/**
 * One service of a bundle, at the rate this client is actually priced at.
 *
 * A rate set on the bundle itself is the package's agreed price for the service
 * and is taken as given. Otherwise the bundle's own `service_default_fee` is only
 * the fall-back for a service the price list does not carry at all, which is a
 * service marked inactive between the bundle being read and the list being resolved.
 */
function pricedBundleItem(item: ServiceBundleItemWithService, rates: RateMap): BundleLineItem {
  const priced = rates.get(item.service_id);
  const rate =
    item.fee != null
      ? { fee: item.fee, origin: "bundle" as const }
      : priced
        ? bestPublishedRate(priced)
        : null;
  const fee = rate ? rate.fee : item.service_default_fee;
  return {
    service_id: item.service_id,
    service_name: item.service_name ?? priced?.name ?? "",
    description: item.description ?? null,
    quantity: String(item.quantity > 0 ? item.quantity : 1),
    amount: fee == null ? "" : String(fee),
    origin: rate?.origin ?? null,
  };
}

/**
 * Re-reads one breakdown row's rate off a price list that has moved.
 *
 * A service the new list does not price keeps the figure it has rather than
 * losing it: the rate was right for the company or date it was read for, and
 * blanking it would quietly drop a service out of the package's figure. A rate
 * the bundle set is not the client's to move, so it is never re-read.
 */
function repricedBundleItem(item: BundleLineItem, rates: RateMap): BundleLineItem {
  if (item.origin === "bundle") return item;
  const priced = rates.get(item.service_id);
  if (!priced) return item;
  const rate = bestPublishedRate(priced);
  return { ...item, amount: String(rate.fee), origin: rate.origin };
}

/**
 * What the breakdown's own figures come to, and which services carry no rate.
 *
 * The unpriced ones come back named rather than counted as zero, for the same
 * reason the catalogue total reports them: a package figure missing one of five
 * services is not the package's price, and only the operator can say what it
 * was sold for.
 */
function bundleLineTotals(items: readonly BundleLineItem[]): {
  total: number;
  unpriced: string[];
} {
  let total = 0;
  const unpriced: string[] = [];

  for (const item of items) {
    if (item.amount === "") {
      unpriced.push(item.service_name.trim() || "an unnamed service");
      continue;
    }
    total += (Number(item.quantity) || 0) * (Number(item.amount) || 0);
  }

  return { total: round2(total), unpriced };
}

/** The breakdown's total as the amount field holds it; blank when it comes to nothing. */
function bundleLineAmount(items: readonly BundleLineItem[]): string {
  const { total } = bundleLineTotals(items);
  return total > 0 ? String(total) : "";
}

/** The breakdown as it is stored on the row, once the typing is done. */
const loggedItem = (item: BundleLineItem): LoggedBundleItem => ({
  service_id: item.service_id || null,
  service_name: item.service_name.trim() || null,
  description: item.description,
  quantity: Number(item.quantity) || 1,
  amount: item.amount === "" ? null : Number(item.amount),
});

/**
 * A whole bundle as one line.
 *
 * Names no service on purpose. It is the package, not any one thing in it, and
 * leaving `service_id` empty is what tells the log — and the invoice — that this
 * row stands for the whole bundle.
 *
 * The amount is what the services in it come to at this client's own rates, and
 * both halves of that are editable: a rate the package was agreed differently for
 * is typed into the breakdown, and a package sold as a round sum is typed into the
 * figure itself, which the invoice then apportions back across the services so the
 * client's lines add up to the price that was agreed.
 */
const bundledLine = (
  items: ServiceBundleItemWithService[],
  bundle: PickedBundle,
  rates: RateMap,
): ServiceLine => {
  const priced = items.map((item) => pricedBundleItem(item, rates));
  return {
    key: nextKey(),
    service_id: "",
    quantity: "1",
    amount: bundleLineAmount(priced),
    description: bundle.name,
    bundle: {
      id: bundle.id,
      name: bundle.name,
      items: priced,
      rates_edited: false,
      total_typed: false,
    },
  };
};

const blankExpenseLine = (): ExpenseLine => ({
  key: nextKey(),
  expense_type: "",
  description: "",
  amount: "",
  markup_pct: "0",
});

/**
 * The expense types on offer: the standing list, plus every type already in use.
 *
 * `expense_type` is free text on the row, not an enum, so a type exists simply
 * by having been used — which is what makes creating one inline durable without
 * a table behind it. The standing list is always offered, so a fresh database
 * is not an empty dropdown.
 *
 * The read is capped like any other list, so a type used only beyond that cap
 * would not be offered. Types are few against expenses, so in practice the cap
 * is reached long after every type in use has been seen.
 */
function useExpenseTypesInUse(enabled: boolean) {
  const { data = [] } = useQuery({
    queryKey: ["expense_types_in_use"],
    // Reads the whole expense column, so it waits until something needs it
    // rather than running on every load of the page.
    enabled,
    queryFn: async () => {
      const { data: rows, error } = await api.workLog.expenses.list().all().select("expense_type");
      if (error) throw error;
      const seen = (rows ?? []) as { expense_type?: string | null }[];
      return [...new Set(seen.map((r) => (r.expense_type ?? "").trim()).filter(Boolean))];
    },
    staleTime: 5 * 60 * 1000,
  });
  return data;
}

/**
 * Expense type picker that can mint the type you are typing.
 *
 * The create option sits *first* and is the one cmdk highlights, so typing a
 * type that does not exist yet and pressing Enter creates and selects it in one
 * motion — the common case when a new kind of cost turns up mid-entry. It is
 * offered only while what is typed matches no existing type, exactly, so Enter
 * on a type that already exists picks it rather than making a duplicate.
 *
 * Unlike the country picker there is no second form to fill: the type *is* its
 * name, so there is nothing left to ask.
 */
function ExpenseTypeField({
  value,
  options,
  onChange,
  onCreate,
}: {
  value: string;
  options: string[];
  onChange: (type: string) => void;
  onCreate: (type: string) => void;
}) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");

  const typed = search.trim();
  const matches = typed
    ? options.filter((o) => o.toLowerCase().includes(typed.toLowerCase()))
    : options;
  const exists = options.some((o) => o.toLowerCase() === typed.toLowerCase());

  const choose = (type: string) => {
    onChange(type);
    setSearch("");
    setOpen(false);
  };

  const create = () => {
    onCreate(typed);
    choose(typed);
  };

  return (
    <Popover
      open={open}
      onOpenChange={(next) => {
        setOpen(next);
        if (!next) setSearch("");
      }}
    >
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className="w-full justify-between font-normal"
        >
          <Truncated
            text={value || "Select type"}
            className={cn("min-w-0 text-left", !value && "text-muted-foreground")}
          />
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[var(--radix-popover-trigger-width)] p-0" align="start">
        {/* Filtered above, so cmdk's own matching is off. */}
        <Command shouldFilter={false}>
          <CommandInput
            placeholder="Search or type a new type…"
            value={search}
            onValueChange={setSearch}
          />
          <CommandList>
            {typed && !exists && (
              <CommandGroup>
                <CommandItem value="__create__" onSelect={create}>
                  <Plus className="mr-2 h-4 w-4" />
                  Create “{typed}”
                </CommandItem>
              </CommandGroup>
            )}

            {matches.length === 0 && (!typed || exists) && (
              <CommandEmpty>No type found.</CommandEmpty>
            )}

            {matches.length > 0 && (
              <CommandGroup>
                {matches.map((o) => (
                  <CommandItem key={o} value={o} onSelect={() => choose(o)}>
                    <Check
                      className={cn(
                        "mr-2 h-4 w-4",
                        o.toLowerCase() === value.toLowerCase() ? "opacity-100" : "opacity-0",
                      )}
                    />
                    {o}
                  </CommandItem>
                ))}
              </CommandGroup>
            )}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}

/**
 * One way in, for both kinds of entry.
 *
 * There were two buttons and two dialogs, which put the choice in the header
 * and made it look like two features. It is one — "record something billable
 * against a company" — so the button is single and the kind is the first field,
 * which is also where the difference actually matters.
 *
 * The height is fixed rather than fitted to the content, and the lines scroll
 * inside it: the two kinds have different numbers of fields, so a dialog sized
 * to its content would resize under you every time you switched kind, and again
 * on every line you added.
 *
 * The two bodies keep their own lines and are mounted one at a time, so
 * switching kind leaves nothing of the other behind.
 */
function LogEntryDialog({
  open,
  onOpenChange,
  initialKind,
  onSavedEvent,
  onSavedExpense,
}: {
  open: boolean;
  onOpenChange: (o: boolean) => void;
  initialKind: LogKind;
  onSavedEvent: () => void;
  onSavedExpense: () => void;
}) {
  const [kind, setKind] = useState<LogKind>(initialKind);
  const inUse = useExpenseTypesInUse(open);
  /** Types minted in this sitting, so a type created on one line serves the next. */
  const [minted, setMinted] = useState<string[]>([]);

  // Follows the tab on each opening, rather than keeping whatever was chosen
  // last time the dialog was up.
  useEffect(() => {
    if (open) setKind(initialKind);
  }, [open, initialKind]);

  const kindToggle = (
    <div className="space-y-2">
      <Label>What are you logging?</Label>
      <div className="grid grid-cols-2 gap-2 rounded-lg bg-muted p-1">
        <Button
          type="button"
          size="sm"
          variant={kind === "service" ? "default" : "ghost"}
          onClick={() => setKind("service")}
        >
          Service delivered
        </Button>
        <Button
          type="button"
          size="sm"
          variant={kind === "expense" ? "default" : "ghost"}
          onClick={() => setKind("expense")}
        >
          Sundry expense
        </Button>
      </div>
    </div>
  );

  const expenseTypes = useMemo(
    () => [...new Set([...EXPENSE_TYPES, ...inUse, ...minted])].sort((a, b) => a.localeCompare(b)),
    [inUse, minted],
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="h-[44rem] max-w-3xl">
        <DialogHeader>
          <DialogTitle>New log entry</DialogTitle>
        </DialogHeader>

        {kind === "service" ? (
          <LogServiceFields
            kindToggle={kindToggle}
            onClose={() => onOpenChange(false)}
            onSaved={onSavedEvent}
          />
        ) : (
          <LogExpenseFields
            kindToggle={kindToggle}
            expenseTypes={expenseTypes}
            onCreateType={(type) => setMinted((m) => [...m, type])}
            onClose={() => onOpenChange(false)}
            onSaved={onSavedExpense}
          />
        )}
      </DialogContent>
    </Dialog>
  );
}

/** The company and date every line in one entry shares. */
function SharedFields({
  companyId,
  onCompany,
  date,
  onDate,
  dateLabel,
}: {
  companyId: string;
  onCompany: (id: string) => void;
  date: string;
  onDate: (date: string) => void;
  dateLabel: string;
}) {
  return (
    <div className="grid grid-cols-1 gap-3 sm:grid-cols-[minmax(0,1fr)_12rem]">
      <div className="space-y-2">
        <Label required>Company</Label>
        <CompanySelect value={companyId} onChange={onCompany} />
      </div>
      <div className="space-y-2">
        <Label required>{dateLabel}</Label>
        <Input type="date" value={date} onChange={(e) => onDate(e.target.value)} />
      </div>
    </div>
  );
}

/**
 * The lines of an entry, as cards in a well.
 *
 * The shared fields around them — company, date, notes — are bare inputs, so
 * without this the lines read as more of the same list of fields rather than as
 * the repeating thing they are. The well is recessed and the cards sit on it,
 * which is what separates "the entry" from "what is on the entry".
 *
 * "Add line" sits at the *bottom*, under the last card: adding is what you do
 * after filling the one above, so a button at the top would mean scrolling up to
 * add and back down to type.
 */
function LineSection({
  label,
  required,
  count,
  addLabel,
  onAdd,
  children,
}: {
  label: string;
  /** Marks the section's label with the required asterisk. */
  required?: boolean;
  count: number;
  addLabel: string;
  onAdd: () => void;
  children: React.ReactNode;
}) {
  return (
    <div className="space-y-3 rounded-xl border bg-muted/40 p-3">
      <div className="flex items-center justify-between px-1">
        <Label required={required}>{label}</Label>
        <span className="text-xs text-muted-foreground">
          {count} line{count === 1 ? "" : "s"}
        </span>
      </div>

      <div className="space-y-3">{children}</div>

      <Button type="button" variant="outline" className="w-full border-dashed" onClick={onAdd}>
        <Plus className="mr-2 h-4 w-4" /> {addLabel}
      </Button>
    </div>
  );
}

/**
 * The columns the breakdown is laid out on, header and rows alike.
 *
 * The last one is the row's own bin, and it is a column rather than something
 * floated over the rate so a long service name cannot push it off the end.
 */
const BUNDLE_GRID = "grid grid-cols-[minmax(0,1fr)_4rem_6.5rem_1.75rem] items-center gap-2";

/**
 * A bundled line: the services the one figure covers, each editable, and the
 * figure itself.
 *
 * In place of the service picker, which a bundled line has no use for — it is the
 * package, not any one thing in it, and re-pointing it at a single service would
 * leave its figure covering a breakdown it no longer matches. Remove the line and
 * pick again, which is what the bin above it is for.
 *
 * The breakdown is the point three times over. It is what the operator checks the
 * package figure against — a total with nothing beside it is a total you cannot
 * verify — it is what the invoice itemises the entry by when it bills, and it is
 * where a rate agreed differently for this client is corrected. So each service
 * carries its own quantity and rate as inputs, with the level the rate came from
 * named underneath — the client group's schedule, the company's own, the Somasi
 * standard list, or the catalogue — because "is this the client's price or the
 * house price?" is the first question the figures raise. A service nothing prices
 * says so rather than reading as free.
 *
 * The package figure sits under the breakdown rather than over it, because that is
 * the order the two are settled in: the services' rates make the figure, and the
 * figure is typed over only where the package was sold for something else.
 */
function BundledLineFields({
  bundle,
  quantity,
  amount,
  onItem,
  onRemoveItem,
  onQuantity,
  onAmount,
}: {
  bundle: BundleLine;
  quantity: string;
  amount: string;
  onItem: (index: number, change: Partial<BundleLineItem>) => void;
  /** Absent on the last service left: a package covering nothing is not a package. */
  onRemoveItem?: (index: number) => void;
  onQuantity: (value: string) => void;
  onAmount: (value: string) => void;
}) {
  const { total, unpriced } = bundleLineTotals(bundle.items);
  const typed = amount === "" ? null : Number(amount);
  // Within half a cent is the same figure, and the invoice treats it as one too.
  const prorated = typed != null && Number.isFinite(typed) && Math.abs(typed - total) >= 0.005;

  return (
    <div className="space-y-3">
      <div className="space-y-2">
        <Label className="text-xs">Bundle</Label>
        <p className="flex items-center gap-1.5 text-sm font-medium">
          <Boxes className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
          <span className="min-w-0 truncate">{bundle.name}</span>
          <Badge variant="outline" className="shrink-0 text-[9px] font-normal">
            bundle
          </Badge>
        </p>
      </div>

      <div className="rounded-md border bg-muted/50 px-2.5 py-2">
        <div className={cn(BUNDLE_GRID, "pb-1 text-[11px] text-muted-foreground")}>
          <span>Service</span>
          <span className="text-right">Qty</span>
          <span className="text-right">Rate</span>
          <span />
        </div>

        {bundle.items.map((item, index) => (
          <div
            key={`${item.service_id || "unnamed"}-${index}`}
            className={cn(BUNDLE_GRID, "border-t py-1.5")}
          >
            <div className="min-w-0">
              <p className="truncate text-sm">{item.service_name || "Unnamed service"}</p>
              <p className="truncate text-[11px] text-muted-foreground">
                {item.origin ? BUNDLE_RATE_LABEL[item.origin] : "No rate published"}
              </p>
            </div>
            <Input
              type="number"
              step="any"
              className="h-8 px-2 text-right"
              value={item.quantity}
              onChange={(e) => onItem(index, { quantity: e.target.value })}
            />
            <Input
              type="number"
              step="any"
              placeholder="No fee"
              className="h-8 px-2 text-right"
              value={item.amount}
              onChange={(e) => onItem(index, { amount: e.target.value })}
            />
            {/* A bundle is what was agreed, not always what was delivered — a
                service held back comes out of the package here rather than
                forcing the whole line to be picked again. The slot is kept even
                with nothing in it, so the inputs above stay in their columns. */}
            {bundle.items.length > 1 && onRemoveItem ? (
              <Button
                type="button"
                variant="ghost"
                size="icon"
                className="h-7 w-7"
                title={`Remove ${item.service_name || "this service"} from the bundle`}
                onClick={() => onRemoveItem(index)}
              >
                <X className="h-3.5 w-3.5 text-muted-foreground" />
              </Button>
            ) : (
              <span />
            )}
          </div>
        ))}

        <div className="flex items-baseline justify-between gap-2 border-t pt-1.5 text-xs">
          <span className="text-muted-foreground">Services add up to</span>
          <span className="font-medium tabular-nums">{money(total)}</span>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-[minmax(0,1fr)_5rem_7rem]">
        <p className="hidden text-xs text-muted-foreground sm:block sm:self-end sm:pb-2.5">
          {prorated
            ? "Billed at the package figure — the invoice spreads it across the services above."
            : "The package figure follows the rates above until you type over it."}
        </p>
        <div className="space-y-2">
          <Label className="text-xs">Bundles</Label>
          <Input
            type="number"
            step="any"
            value={quantity}
            onChange={(e) => onQuantity(e.target.value)}
          />
        </div>
        <div className="space-y-2">
          <Label className="text-xs">Price each</Label>
          <Input
            type="number"
            step="any"
            placeholder="Optional"
            value={amount}
            onChange={(e) => onAmount(e.target.value)}
          />
        </div>
      </div>

      {prorated && (
        <p className="text-xs text-muted-foreground">
          Logged at {money(typed ?? 0)} against {money(total)} for the services — the difference is
          spread across them when the invoice itemises the bundle, so the client's lines add up to
          the price that was agreed.
        </p>
      )}
      {unpriced.length > 0 && (
        <p className="text-xs text-muted-foreground">
          No rate for {unpriced.join(", ")} — the package figure does not cover{" "}
          {unpriced.length === 1 ? "it" : "them"}.
        </p>
      )}
    </div>
  );
}

/**
 * One line, raised off the well it sits on.
 *
 * `onRemove` is absent where there is nothing worth removing — the last blank
 * service card — and present on everything else, a bundled line included even
 * when it is the only line on the entry.
 */
function LineCard({
  index,
  onRemove,
  children,
}: {
  index: number;
  onRemove?: () => void;
  children: React.ReactNode;
}) {
  return (
    <div className="rounded-lg border bg-card p-3 shadow-sm">
      <div className="mb-2 flex items-center justify-between">
        <span className="text-xs font-medium text-muted-foreground">Line {index}</span>
        {onRemove && (
          <Button type="button" variant="ghost" size="icon" className="h-7 w-7" onClick={onRemove}>
            <Trash2 className="h-4 w-4 text-destructive" />
          </Button>
        )}
      </div>
      {children}
    </div>
  );
}

/**
 * Everything between the dialog's header and its footer, as one scroll region.
 *
 * It takes the height the rest of the dialog leaves and scrolls within it, so
 * the dialog itself never grows — adding a line lengthens the list, not the
 * dialog. The whole body scrolls rather than the list alone: scrolling only the
 * lines left the company, date and notes pinned around a small moving window,
 * and put a second scrollbar inside the first.
 */
function EntryBody({ children }: { children: React.ReactNode }) {
  return <div className="min-h-0 flex-1 space-y-4 overflow-y-auto pr-1">{children}</div>;
}

function LogServiceFields({
  kindToggle,
  onClose,
  onSaved,
}: {
  kindToggle: React.ReactNode;
  onClose: () => void;
  onSaved: () => void;
}) {
  const today = new Date().toISOString().slice(0, 10);
  const [saving, setSaving] = useState(false);
  const [companyId, setCompanyId] = useState("");
  const [eventDate, setEventDate] = useState(today);
  const [notes, setNotes] = useState("");
  const [lines, setLines] = useState<ServiceLine[]>([blankServiceLine()]);

  /**
   * What this client pays for each service, as at the date delivered.
   *
   * A bundle is picked, not typed, so its figures have to come from somewhere, and
   * the catalogue's own fees are the wrong somewhere for a client on a negotiated
   * schedule: the package would be logged at a price nobody agreed, and the
   * invoice raised from it would bill that price. This is the same read the new
   * invoice page makes — one request that runs the whole cascade, company fee then
   * company schedule then client group then catalogue — so the log and the invoice
   * cannot disagree about what a service costs.
   *
   * Dated by the delivery date rather than today: each schedule holds a version
   * per period, and work logged into a past month is priced at that month's rates.
   */
  const { data: priceList = [] } = useQuery({
    queryKey: ["price_list", companyId, eventDate],
    enabled: !!companyId,
    queryFn: () => resolveCompanyPriceList(companyId, eventDate),
  });

  const rates = useMemo<RateMap>(
    () => new Map(priceList.map((p) => [p.service_id, p])),
    [priceList],
  );

  /**
   * Re-reads the rates of every bundled line nobody has typed over.
   *
   * Correcting the company or the delivery date after a bundle was added resolves a
   * different price list, and a package still carrying the previous client's rates
   * is a package billed at a price that client never agreed. A line whose figures
   * have been edited is left exactly as it is — that is the operator's answer about
   * what was agreed, and it outranks any schedule.
   */
  useEffect(() => {
    if (rates.size === 0) return;
    setLines((ls) =>
      ls.map((l) => {
        if (!l.bundle || l.bundle.rates_edited) return l;
        const items = l.bundle.items.map((item) => repricedBundleItem(item, rates));
        return {
          ...l,
          amount: l.bundle.total_typed ? l.amount : bundleLineAmount(items),
          bundle: { ...l.bundle, items },
        };
      }),
    );
  }, [rates]);

  const patch = (key: string, change: Partial<ServiceLine>) =>
    setLines((ls) => ls.map((l) => (l.key === key ? { ...l, ...change } : l)));

  /**
   * Edits one service of a bundled line.
   *
   * The package figure follows the breakdown it is made of, so a corrected rate
   * moves the line's own amount with it — unless that amount has been typed over,
   * in which case the package was sold for a figure of its own and the breakdown
   * is only how the invoice divides it up.
   */
  const patchItem = (key: string, index: number, change: Partial<BundleLineItem>) =>
    setLines((ls) =>
      ls.map((l) => {
        if (l.key !== key || !l.bundle) return l;
        const items = l.bundle.items.map((item, i) =>
          i === index ? { ...item, ...change } : item,
        );
        return {
          ...l,
          amount: l.bundle.total_typed ? l.amount : bundleLineAmount(items),
          bundle: { ...l.bundle, items, rates_edited: true },
        };
      }),
    );

  /**
   * Drops one service out of a bundled line's package.
   *
   * The figure follows, because the package no longer covers that service —
   * unless it was typed over, in which case the agreed price stands and the
   * breakdown is only how the invoice divides it up. The rates of what is left
   * are untouched, so they still follow the client's price list.
   */
  const removeItem = (key: string, index: number) =>
    setLines((ls) =>
      ls.map((l) => {
        if (l.key !== key || !l.bundle || l.bundle.items.length <= 1) return l;
        const items = l.bundle.items.filter((_, i) => i !== index);
        return {
          ...l,
          amount: l.bundle.total_typed ? l.amount : bundleLineAmount(items),
          bundle: { ...l.bundle, items },
        };
      }),
    );

  /**
   * Takes a line off the entry, leaving a blank one where it was the last.
   *
   * Which is what makes a bundled line removable at all: a bundle usually arrives
   * into the single untouched card the dialog opens with and replaces it, so it is
   * the only line there is — and hiding the bin on the only line left the operator
   * with a package they could not take back off the entry.
   */
  const removeLine = (key: string) =>
    setLines((ls) => {
      const left = ls.filter((l) => l.key !== key);
      return left.length > 0 ? left : [blankServiceLine()];
    });

  /** The package figure, typed over the breakdown's own total — see `BundleLine`. */
  const patchBundleAmount = (key: string, amount: string) =>
    setLines((ls) =>
      ls.map((l) =>
        l.key === key && l.bundle
          ? { ...l, amount, bundle: { ...l.bundle, total_typed: true } }
          : l,
      ),
    );

  /**
   * Picking a service fills that line's description and amount from the
   * catalogue, without overwriting anything already typed on it.
   *
   * The service's own row comes back with the chosen option rather than being
   * looked up in a downloaded catalogue, which is what lets the picker search
   * the API instead of holding every service in memory.
   */
  const pickService = (key: string, s: ServiceRow) => {
    setLines((ls) =>
      ls.map((l) =>
        l.key === key
          ? {
              ...l,
              service_id: s.id,
              description: l.description || s.invoice_description || s.name,
              amount: l.amount || (s.default_fee != null ? String(s.default_fee) : ""),
            }
          : l,
      ),
    );
  };

  /**
   * Puts a bundle on the entry, as one line for the whole package.
   *
   * One line because that is what a bundle is: one package delivered at one agreed
   * price, which is one fact and should be one row in the log. It hides nothing —
   * the breakdown goes on the row, every service in it is editable before the
   * entry is saved, and the invoice itemises the package back into its services
   * when it bills.
   *
   * The line names no service, because it is not one service, and that is also how
   * the log tells a bundled row from an ordinary one later. Its figures are this
   * client's own rates, service by service, with the level each came from named
   * beside it; a service nothing prices contributes nothing and is said out loud,
   * because a figure missing one of five services is not the package's price and
   * only the operator knows what it was sold for.
   *
   * Appended, not substituted: a bundle is usually the bulk of an entry and the
   * odd extra service is typed beside it, so taking a bundle must not discard what
   * is already keyed in. The one exception is a single blank line nobody has
   * touched, which is the state the dialog opens in — keeping it would leave an
   * empty card above the bundle for the operator to notice and delete.
   */
  const expandBundle = (items: ServiceBundleItemWithService[], bundle: PickedBundle) => {
    const added = bundledLine(items, bundle, rates);

    setLines((ls) => {
      const untouched =
        ls.length === 1 &&
        !ls[0].service_id &&
        !ls[0].bundle &&
        !ls[0].description.trim() &&
        ls[0].amount === "";
      return untouched ? [added] : [...ls, added];
    });

    const { unpriced } = bundleLineTotals(added.bundle?.items ?? []);
    if (unpriced.length > 0) {
      notify.warning(
        `No rate for ${unpriced.join(", ")} — the bundle's figure does not cover ` +
          `${unpriced.length === 1 ? "it" : "them"}`,
      );
    }
  };

  const total = lines.reduce(
    (sum, l) => sum + (Number(l.quantity) || 0) * (Number(l.amount) || 0),
    0,
  );

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!companyId) return notify.error("Pick a company");

    // A line with nothing on it is treated as one the operator started and
    // abandoned, not as an error — only the filled ones are saved.
    const filled = lines.filter(
      (l) => l.service_id || l.bundle || l.description.trim() || l.amount !== "",
    );
    if (filled.length === 0) return notify.error("Add at least one service line");

    for (const [i, l] of filled.entries()) {
      if (!l.service_id && !l.bundle && !l.description.trim()) {
        return notify.error(`Line ${i + 1}: pick a service or describe the work`);
      }
      // A bundled line is the only thing naming what was delivered — it has no
      // service behind it to fall back on.
      if (l.bundle && !l.description.trim()) {
        return notify.error(`Line ${i + 1}: the bundled line needs a description`);
      }
      if (l.quantity !== "" && !(Number(l.quantity) > 0)) {
        return notify.error(`Line ${i + 1}: quantity must be greater than zero`);
      }
      // The breakdown is editable, so it is checked like anything typed in. A
      // blank rate is allowed and means the package figure does not cover that
      // service; a blank or negative quantity is not, because the row would be
      // refused on the way in and the whole entry would fail at the server.
      for (const item of l.bundle?.items ?? []) {
        const service = item.service_name.trim() || "a service in the bundle";
        if (!(Number(item.quantity) > 0)) {
          return notify.error(`Line ${i + 1}, ${service}: quantity must be greater than zero`);
        }
        if (item.amount !== "" && !(Number(item.amount) >= 0)) {
          return notify.error(`Line ${i + 1}, ${service}: the rate cannot be negative`);
        }
      }
    }

    setSaving(true);
    const { data, error } = await api.workLog.events.createMany(
      filled.map((l) => ({
        company_id: companyId,
        service_id: l.service_id || null,
        event_date: eventDate,
        quantity: Number(l.quantity) || 1,
        description: l.description.trim() || null,
        amount: l.amount === "" ? null : Number(l.amount),
        notes: notes || null,
        // The stamp that lets the log tell a delivered bundle from five services
        // keyed in together. `bundle_name` is not sent — the server copies it off
        // the bundle this id names.
        bundle_id: l.bundle?.id ?? null,
        bundle_items: l.bundle ? l.bundle.items.map(loggedItem) : null,
      })),
    );
    setSaving(false);
    if (error) return notify.error(error);

    const saved = data?.length ?? filled.length;
    notify.success(
      `${saved} service${saved === 1 ? "" : "s"} logged — ${saved === 1 ? "it" : "they"} will appear on the next invoice for this company`,
    );
    onClose();
    onSaved();
  };

  return (
    <form onSubmit={submit} className="flex min-h-0 flex-1 flex-col">
      <EntryBody>
        {kindToggle}

        <SharedFields
          companyId={companyId}
          onCompany={setCompanyId}
          date={eventDate}
          onDate={setEventDate}
          dateLabel="Date delivered"
        />

        {/* Above the lines, not inside them: it is a way of filling the section
            rather than one more line in it, and the line it produces is an
            ordinary editable card once it arrives.

            Disabled until there is a company, because the figures a bundle
            arrives at are that company's own rates. Expanding one first would
            price the package at the catalogue and leave the operator to notice
            that it had. */}
        <div className="space-y-2 rounded-xl border bg-muted/40 p-3">
          <BundlePicker
            onExpand={expandBundle}
            mode="grouped"
            disabled={!companyId}
            hint={
              companyId
                ? "Adds one line for the whole bundle, priced at this client's own rates as at the date delivered. Every service in it stays editable, and the invoice itemises the package back into them."
                : "Pick a company first — a bundle is priced at that client's own rates."
            }
          />
        </div>

        <LineSection
          label="Services delivered"
          required
          count={lines.length}
          addLabel="Add service line"
          onAdd={() => setLines((ls) => [...ls, blankServiceLine()])}
        >
          {lines.map((l, i) => (
            <LineCard
              key={l.key}
              index={i + 1}
              // A bundled line keeps its bin even as the only line on the entry;
              // an empty card is the one thing nobody needs a bin for.
              onRemove={lines.length > 1 || l.bundle ? () => removeLine(l.key) : undefined}
            >
              {l.bundle ? (
                <BundledLineFields
                  bundle={l.bundle}
                  quantity={l.quantity}
                  amount={l.amount}
                  onItem={(index, change) => patchItem(l.key, index, change)}
                  onRemoveItem={(index) => removeItem(l.key, index)}
                  onQuantity={(v) => patch(l.key, { quantity: v })}
                  onAmount={(v) => patchBundleAmount(l.key, v)}
                />
              ) : (
                <div className="grid grid-cols-1 gap-3 sm:grid-cols-[minmax(0,1fr)_5rem_7rem]">
                  <div className="space-y-2">
                    <Label className="text-xs">Service</Label>
                    <ServiceSelect
                      value={l.service_id}
                      onChange={(id) => patch(l.key, { service_id: id })}
                      onPick={(o) => o.row && pickService(l.key, o.row)}
                      contentClassName="w-80 p-0"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-xs">Qty</Label>
                    <Input
                      type="number"
                      step="any"
                      value={l.quantity}
                      onChange={(e) => patch(l.key, { quantity: e.target.value })}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-xs">Amount</Label>
                    <Input
                      type="number"
                      step="any"
                      placeholder="Optional"
                      value={l.amount}
                      onChange={(e) => patch(l.key, { amount: e.target.value })}
                    />
                  </div>
                </div>
              )}

              <div className="mt-3 space-y-2">
                <Label className="text-xs" required={!!l.bundle}>
                  Invoice description
                </Label>
                <Input
                  value={l.description}
                  onChange={(e) => patch(l.key, { description: e.target.value })}
                  placeholder="Editable again at invoice time"
                />
              </div>
            </LineCard>
          ))}
        </LineSection>

        <div className="space-y-2">
          <Label>Internal notes</Label>
          <Textarea
            rows={2}
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="Kept on every line in this entry"
          />
        </div>
      </EntryBody>

      <DialogFooter className="items-center sm:justify-between">
        <span className="text-sm text-muted-foreground">
          {total > 0 ? `USD ${total.toFixed(2)}` : ""}
        </span>
        <div className="flex gap-2">
          <Button type="button" variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit" disabled={saving}>
            {saving ? "Saving…" : "Log services"}
          </Button>
        </div>
      </DialogFooter>
    </form>
  );
}

function LogExpenseFields({
  kindToggle,
  expenseTypes,
  onCreateType,
  onClose,
  onSaved,
}: {
  kindToggle: React.ReactNode;
  expenseTypes: string[];
  onCreateType: (type: string) => void;
  onClose: () => void;
  onSaved: () => void;
}) {
  const today = new Date().toISOString().slice(0, 10);
  const [saving, setSaving] = useState(false);
  const [companyId, setCompanyId] = useState("");
  const [expenseDate, setExpenseDate] = useState(today);
  const [notes, setNotes] = useState("");
  const [lines, setLines] = useState<ExpenseLine[]>([blankExpenseLine()]);

  const patch = (key: string, change: Partial<ExpenseLine>) =>
    setLines((ls) => ls.map((l) => (l.key === key ? { ...l, ...change } : l)));

  const total = lines.reduce(
    (sum, l) => sum + (Number(l.amount) || 0) * (1 + (Number(l.markup_pct) || 0) / 100),
    0,
  );

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!companyId) return notify.error("Pick a company");

    const filled = lines.filter((l) => l.expense_type || l.description.trim() || l.amount !== "");
    if (filled.length === 0) return notify.error("Add at least one expense line");

    for (const [i, l] of filled.entries()) {
      if (!l.expense_type) return notify.error(`Line ${i + 1}: pick or create a type`);
      if (!l.description.trim()) return notify.error(`Line ${i + 1}: add a short description`);
      if (l.amount === "" || !(Number(l.amount) >= 0)) {
        return notify.error(`Line ${i + 1}: enter the amount paid`);
      }
    }

    setSaving(true);
    const { data, error } = await api.workLog.expenses.createMany(
      filled.map((l) => ({
        company_id: companyId,
        expense_date: expenseDate,
        expense_type: l.expense_type,
        description: l.description.trim(),
        amount: Number(l.amount) || 0,
        markup_pct: Number(l.markup_pct) || 0,
        notes: notes || null,
      })),
    );
    setSaving(false);
    if (error) return notify.error(error);

    const saved = data?.length ?? filled.length;
    notify.success(
      `${saved} expense${saved === 1 ? "" : "s"} recorded — ${saved === 1 ? "it" : "they"} will show as recoverable on the next invoice`,
    );
    onClose();
    onSaved();
  };

  return (
    <form onSubmit={submit} className="flex min-h-0 flex-1 flex-col">
      <EntryBody>
        {kindToggle}

        <SharedFields
          companyId={companyId}
          onCompany={setCompanyId}
          date={expenseDate}
          onDate={setExpenseDate}
          dateLabel="Date incurred"
        />

        <LineSection
          label="Expenses"
          required
          count={lines.length}
          addLabel="Add expense line"
          onAdd={() => setLines((ls) => [...ls, blankExpenseLine()])}
        >
          {lines.map((l, i) => (
            <LineCard
              key={l.key}
              index={i + 1}
              onRemove={
                lines.length > 1
                  ? () => setLines((ls) => ls.filter((x) => x.key !== l.key))
                  : undefined
              }
            >
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-[minmax(0,1fr)_7rem_5rem]">
                <div className="space-y-2">
                  <Label className="text-xs" required>
                    Type
                  </Label>
                  <ExpenseTypeField
                    value={l.expense_type}
                    options={expenseTypes}
                    onChange={(type) => patch(l.key, { expense_type: type })}
                    onCreate={onCreateType}
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-xs" required>
                    Amount
                  </Label>
                  <Input
                    type="number"
                    step="any"
                    value={l.amount}
                    onChange={(e) => patch(l.key, { amount: e.target.value })}
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-xs">Markup %</Label>
                  <Input
                    type="number"
                    step="any"
                    value={l.markup_pct}
                    onChange={(e) => patch(l.key, { markup_pct: e.target.value })}
                  />
                </div>
              </div>

              <div className="mt-3 space-y-2">
                <Label className="text-xs" required>
                  Description
                </Label>
                <Input
                  value={l.description}
                  onChange={(e) => patch(l.key, { description: e.target.value })}
                />
              </div>
            </LineCard>
          ))}
        </LineSection>

        <div className="space-y-2">
          <Label>Notes</Label>
          <Textarea
            rows={2}
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="Kept on every line in this entry"
          />
        </div>
      </EntryBody>

      <DialogFooter className="items-center sm:justify-between">
        <span className="text-sm text-muted-foreground">
          {total > 0 ? `USD ${total.toFixed(2)} recoverable` : ""}
        </span>
        <div className="flex gap-2">
          <Button type="button" variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit" disabled={saving}>
            {saving ? "Saving…" : "Record expenses"}
          </Button>
        </div>
      </DialogFooter>
    </form>
  );
}
